<template>
  <PageContainer title="Envio de Aulas ao GETRAN">
    <Box class="send-container" space="medium">
      <form @submit.prevent="getAulas()">
        <div class="buttons">
          <Button type="button" text="Pesquisa no GETRAN" color="secondary" @handle:click="openConsultaGetran"
            :disabled="loading">
            <DatabaseSearch :size="18" />
          </Button>

          <Button type="button" text="Limpar Status e Enviar (Artisan)" color="primary" @handle:click="sendByArtisan()"
            :disabled="loading">
            <Cog :size="18" />
          </Button>
        </div>

        <div class="inputs">
          <div class="inputs-row">
            <Select class="input-full" :options="cfcs" :modelValue="selectedCFC"
              @update:modelValue="$event => selectedCFC = $event" legend="Autoescola"
              placeholder="Selecione uma autoescola" :disabled="loadTable || loading" 
              data-testid="select-autoescola" />

            <Period legend="Período de Consulta" :firstModelValue="dataInicio" :secondModelValue="dataFim"
              @update:firstModelValue="$event => (dataInicio = $event)"
              @update:secondModelValue="$event => (dataFim = $event)" :disabled="loadTable || loading" />
          </div>

          <div class="inputs-row">
            <Select :options="statusList" :modelValue="selectedStatus"
              @update:modelValue="$event => selectedStatus = $event" legend="Status" placeholder="Selecione um status"
              :disabled="loadTable || loading" class="input-full" />

            <Input legend="Nome do Aluno"
              :placeholder="!selectedCFC ? 'Selecione uma autoescola primeiro' : 'Digite o nome do aluno'"
              :modelValue="nomeAluno" @update:modelValue="$event => nomeAluno = $event" class="input-full"
              :disabled="loadTable || loading || !selectedCFC" />

            <Input mask="###.###.###-##" legend="CPF do aluno"
              :placeholder="!selectedCFC ? 'Selecione uma autoescola primeiro' : '000.000.000-00'"
              :modelValue="cpfAluno" @update:modelValue="$event => cpfAluno = $event" class="input-full"
              :disabled="loadTable || loading || !selectedCFC" />

            <Input data-testid="input-id-aula" legend="ID da Aula" type="number"
              :placeholder="!selectedCFC ? 'Selecione uma autoescola primeiro' : 'Digite o ID da aula'"
              :modelValue="idAula" @update:modelValue="$event => idAula = $event" class="input-full"
              :disabled="loadTable || loading || !selectedCFC" />
          </div>
        </div>

        <div class="buttons">
          <Button type="button" @handle:click="clearFilters" text="Limpar" :fill="false" color="danger"
            :disabled="loading">
            <CloseCircleOutline :size="18" />
          </Button>

          <Button type="submit" text="Consultar" color="primary" :disabled="loading" data-testid="button-consultar-aulas">
            <Magnify :size="18" />
          </Button>
        </div>
      </form>

      <div v-if="loading" class="loading-container">
        <LoadingSpinner />
      </div>

      <div v-else-if="!loading && aulas !== null && (!aulasFiltradas || aulasFiltradas.length === 0)" class="empty-state">
        <FileDocumentAlertOutline :size="64" />
        <h3>Nenhum registro encontrado</h3>
        <p>Não foram encontradas aulas com os filtros selecionados. Tente ajustar os critérios de busca.</p>
      </div>

      <div class="table-container" v-else-if="!loading && aulasFiltradas && aulasFiltradas.length > 0">
        <Paginate :total-pages="aulas.last_page" :current-page="aulas.current_page" :loading="loading"
          @page-changed="navigate" />

        <Table :headers="['ID', 'Status', 'Aluno', 'CPF', 'Renach', 'Instrutor', 'Data', 'Categoria', 'Ações']">
          <tr v-for="(aula, index) in aulasFiltradas" :key="index">
            <td>{{ aula.aulaPraticaId }}</td>
            <td>
              <Badge :text="getStatusText(aula.aulaPraticaStatus)" :color="getStatusColor(aula.aulaPraticaStatus)" />
            </td>
            <td>{{ aula.alunoNome }}</td>
            <td>{{ utils.formatarCPF(aula.alunoCPF) }}</td>
            <td>{{ aula.renach || '-' }}</td>
            <td>{{ aula.instrutorNome }}</td>
            <td>{{ utils.formatDate(aula.aulaPraticaData) }}</td>
            <td>{{ aula.categoria || '-' }}</td>
            <td>
              <div class="actions">
                <div v-if="canSendAula(aula)">
                  <IconButton @handle:click="sendClassroom(aula)" color="primary" title="Enviar Aula"
                    :disabled="loadingActions">
                    <Send :size="16" />
                  </IconButton>
                </div>

                <div>
                  <IconButton @handle:click="abrirModificacaoAula(aula.aulaPraticaId, aula.schema)" color="secondary"
                    title="Modificar aula" :disabled="loadingActions">
                    <Wrench :size="16" />
                  </IconButton>
                </div>

                <div>
                  <IconButton @handle:click="abrirAulasDoInstrutor(aula.instrutorId, aula.aulaPraticaData, aula.schema)"
                    color="primary" title="Visualizar aulas do instrutor" :disabled="loadingActions">
                    <FormatListBulleted :size="16" />
                  </IconButton>
                </div>

                <div>
                  <IconButton @handle:click="abrirAulasDoAluno(aula.alunoId, aula.aulaPraticaId, aula.schema)"
                    color="secondary" title="Visualizar aulas do aluno" :disabled="loadingActions">
                    <Eye :size="16" />
                  </IconButton>
                </div>

                <div>
                  <IconButton @handle:click="openConfirmModal(aula)" color="danger" title="Remover Anotação"
                    :disabled="loadingActions">
                    <Close :size="16" />
                  </IconButton>
                </div>
              </div>
            </td>
          </tr>
        </Table>
      </div>
    </Box>

    <Modal ref="aulaModal" title="Modificar Aula Prática" @cancel="closeAulaModal" v-if="aulaModal">
      <aula-modal :aula="aulaModal.aula" :instrutores="aulaModal.instrutores" :veiculos="aulaModal.veiculos"
        v-on:close="closeAulaModal()" v-on:closeandupdate="updateAndCloseModalAluno()" />
    </Modal>

    <Modal ref="aulasInstrutorModal" title="Aulas do Instrutor" @cancel="fecharAulaInstrutor"
      v-if="aulasInstrutorModal">
      <aulas-instrutor-modal :instrutor="aulasInstrutorModal.instrutor" :schema="aulasInstrutorModal.schema"
        :data="aulasInstrutorModal.data" v-on:close="fecharAulaInstrutor()" />
    </Modal>

    <Modal ref="aulasAlunoModal" title="Histórico de Aulas do Aluno" @cancel="$refs.aulasAlunoModal.close()"
      v-if="aulasAlunoModal">
      <aulas-aluno-modal :aulas="aulasAlunoModal.aulas" :selected="aulasAlunoModal.selected"
        :schema="aulasAlunoModal.schema" v-on:close="$refs.aulasAlunoModal.close()" />
    </Modal>

    <Modal ref="consultaGetranModal" title="Consulta GETRAN" @cancel="closeConsultaGetran" v-if="consultaGetranModal">
      <consulta-getran-modal v-on:close="closeConsultaGetran" :autoescolas="cfcs" />
    </Modal>

    <vuestic-modal v-if="envioModal" ref="envioModal" :noButtons="true">
      <envio-modal :aula="selectedEnvioModal" :ajuste="ajuste" v-on:close="$refs.envioModal.close()" />
    </vuestic-modal>

    <ConfirmModal ref="confirmModal" title="Cancelar cobrança"
      message="Deseja mesmo remover a anotação? A operação é irreversível." :onConfirm="removerAnotacao" />
  </PageContainer>
</template>

<script>
import Vue from 'vue'
import urlApi from '@/router/urlApiV2'
import utils from '@/services/utils.js'

// Componentes Core
import Box from 'core/Box'
import Button from 'core/Button'
import IconButton from 'core/IconButton'
import LoadingSpinner from 'core/LoadingSpinner'
import PageContainer from 'core/PageContainer'
import Paginate from 'core/Paginate'
import Select from 'core/Select'
import Table from 'core/Table'
import Period from 'core/Period'
import Input from 'core/Input'
import Badge from 'core/Badge'
import Modal from 'core/Modal'
import ConfirmModal from 'core/ConfirmModal'

// Ícones
import Magnify from 'icons/Magnify'
import DatabaseSearch from 'icons/DatabaseSearch'
import Cog from 'icons/Cog'
import Send from 'icons/Send'
import Wrench from 'icons/Wrench'
import FormatListBulleted from 'icons/FormatListBulleted'
import Eye from 'icons/Eye'
import Close from 'icons/Close'
import FileDocumentAlertOutline from 'icons/FileDocumentAlertOutline'
import FilterRemove from 'icons/FilterRemove'
import CloseCircleOutline from 'icons/CloseCircleOutline'

// Componentes específicos do módulo
import AulaModal from './components/AulaModal.vue'
import EnvioModal from './components/EnvioModal.vue'
import AulasInstrutorModal from './components/AulasInstrutorModal.vue'
import AulasAlunoModal from './components/AulasAlunoModal.vue'
import ConsultaGetranModal from './components/ConsultaGetranModal.vue'
import ModernCardAula from './components/ModernCardAula.vue'

export default {
  name: 'EnvioAulas',
  components: {
    // Core
    Box,
    Button,
    IconButton,
    LoadingSpinner,
    PageContainer,
    Paginate,
    Select,
    Period,
    Input,
    Badge,
    Table,
    Modal,
    ConfirmModal,

    // Ícones
    Magnify,
    DatabaseSearch,
    Cog,
    Send,
    Wrench,
    FormatListBulleted,
    Eye,
    Close,
    FileDocumentAlertOutline,
    FilterRemove,
    CloseCircleOutline,

    // Componentes específicos
    AulaModal,
    EnvioModal,
    AulasInstrutorModal,
    AulasAlunoModal,
    ConsultaGetranModal,
    ModernCardAula
  },
  data() {
    return {
      utils: utils,
      schema: null,
      loadTable: false,
      aulaModal: null,
      aulasInstrutorModal: null,
      aulasAlunoModal: null,
      loading: false,
      aulas: null,
      cfcs: [{ value: '', text: 'Selecione uma autoescola' }],
      selectedCFC: '',
      dataInicio: '',
      dataFim: '',
      idAula: '',
      statusList: [
        {
          value: 'todos',
          text: 'Todos os status'
        },
        {
          value: 'Aceito',
          text: 'Aceita'
        },
        {
          value: 'Em análise',
          text: 'Em Análise'
        },
        {
          value: null,
          text: 'Pendentes'
        },
        { value: 'Aula com mais de 1 hora', text: 'Aula com mais de 1 hora' },
        { value: 'Aula dupla não possui tempo mínimo necessário', text: 'Aula dupla não possui tempo mínimo necessário' },
        { value: 'Aula já foi enviada', text: 'Aula já foi enviada' },
        { value: 'Categoria do instrutor não compatível com a categoria pretendida do candidato', text: 'Categoria do instrutor não compatível com a categoria pretendida do candidato' },
        { value: 'Data final da LADV menor que a data da aula informada', text: 'Data final da LADV menor que a data da aula informada' },
        { value: 'É permitido apenas 1 aula de conceitos básicos', text: 'É permitido apenas 1 aula de conceitos básicos' },
        { value: 'É permitido apenas 5 aulas de parada e estacionamento', text: 'É permitido apenas 5 aulas de parada e estacionamento' },
        { value: 'Erro ao retornar consulta. Contate o suporte', text: 'Erro ao retornar consulta. Contate o suporte' },
        { value: 'Houve um erro interno', text: 'Houve um erro interno' },
        { value: 'Instrutor do posto avançado não cadastrado', text: 'Instrutor do posto avançado não cadastrado' },
        { value: 'Instrutor não encontrado', text: 'Instrutor não encontrado' },
        { value: 'Instrutor não pode lecionar mais que 24 horas diárias', text: 'Instrutor não pode lecionar mais que 24 horas diárias' },
        { value: 'Instrutor já possui aula para a data e horário informado', text: 'Instrutor já possui aula para a data e horário informado' },
        { value: 'Instrutor só pode lecionar 2 alunos simultaneamente', text: 'Instrutor só pode lecionar 2 alunos simultaneamente' },
        { value: 'Já existe uma aula para este horário informado', text: 'Já existe uma aula para este horário informado' },
        { value: 'Não é possível inserir terceira aula com intervalo menor que 50 minutos', text: 'Não é possível inserir terceira aula com intervalo menor que 50 minutos' },
        { value: 'Não Enviado', text: 'Não Enviado' },
        { value: 'Não foi possível consultar dados do veículo informado', text: 'Não foi possível consultar dados do veículo informado' },
        { value: 'O tempo total da aula está com menos de quarenta e cinco minutos', text: 'O tempo total da aula está com menos de quarenta e cinco minutos' },
        { value: 'Processo não existe', text: 'Processo não existe' },
        { value: 'Esta aula já foi recebida e está aguardando auditoria', text: 'Esta aula já foi recebida e está aguardando auditoria' },
        { value: 'Esta aula será analisada pela CRT', text: 'Esta aula será analisada pela CRT' },
        { value: 'Timeout ao consultar veículo', text: 'Timeout ao consultar veículo' },
        { value: 'Transação efetuada com sucesso', text: 'Transação efetuada com sucesso' },
        { value: 'Erro ao retornar consulta. Contate o suporte', text: 'Erro ao retornar consulta. Contate o suporte' },
        { value: 'Houve um erro interno', text: 'Houve um erro interno' },
        { value: 'Instrutor não pode lecionar mais que 12 horas diárias', text: 'Instrutor não pode lecionar mais que 12 horas diárias' },
        { value: 'O veículo não está associado ao CFC na data aula informada', text: 'O veículo não está associado ao CFC na data aula informada' },
      ],
      selectedStatus: 'todos',
      nomeAluno: '',
      cpfAluno: '',
      consultaGetranModal: false,
      envioModal: false,
      selectedEnvioModal: {},
      loadBtn: false,
      ajuste: false,
      statusEnviados: ['Em análise', 'Aceito', 'Transação efetuada com sucesso'],
      loadingActions: false,
    }
  },
  computed: {
    aulasFiltradas() {
      return this.aulas && this.aulas.data ? this.aulas.data : []
    },
  },
  mounted() {
    this.getCfcs()
  },
  methods: {
    canSendAula(aula) {
      const status = aula.aulaPraticaStatus
      return !this.statusEnviados.includes(status)
    },

    navigate(page) {
      this.loading = true
      this.getAulas(page)
    },

    async getAulas(page = 1) {
      this.loading = true

      const params = {
        inicio: this.dataInicio,
        fim: this.dataFim,
        schema: this.selectedCFC,
        status: this.selectedStatus,
        nome: this.selectedCFC ? this.nomeAluno.trim() : null,
        cpf: this.selectedCFC ? this.cpfAluno.replace(/\D/g, '') : null,
        idAula: this.idAula ? this.idAula.toString().trim() : null,
        page
      }

      try {
        const response = await this.$http.get(`${urlApi}/dashboard/aulas/praticas/envios`, { params })

        if (response.ok) {
          this.aulas = response.body
          this.schema = this.selectedCFC
        } else {
          const errorMsg = (response.body && response.body.mensagem) || (response.body && response.body.msg) || 'Erro ao consultar aulas'
          this.$toasted.show(errorMsg, {
            position: 'top-right',
            type: 'error',
            duration: 5000
          })
        }
      } catch (error) {
        console.error('Erro ao buscar aulas:', error)
        let errorMessage = 'Erro ao consultar aulas'
        
        if (error.body && error.body.mensagem) {
          errorMessage = error.body.mensagem
        } else if (error.body && error.body.msg) {
          errorMessage = error.body.msg
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.$toasted.show(errorMessage, {
          position: 'top-right',
          type: 'error',
          duration: 5000
        })
      } finally {
        this.loading = false
      }
    },

    clearFilters() {
      this.selectedCFC = ''
      this.dataInicio = ''
      this.dataFim = ''
      this.selectedStatus = 'todos'
      this.nomeAluno = ''
      this.cpfAluno = ''
      this.idAula = ''
      this.aulas = null
    },

    formatCPF() {
      let value = this.cpfAluno.replace(/\D/g, '')
      value = value.replace(/(\d{3})(\d)/, '$1.$2')
      value = value.replace(/(\d{3})(\d)/, '$1.$2')
      value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      this.cpfAluno = value
    },

    openConsultaGetran() {
      this.consultaGetranModal = true
      this.$nextTick(() => {
        this.$refs.consultaGetranModal.open()
      })
    },

    closeConsultaGetran() {
      this.consultaGetranModal = false
      if (this.$refs.consultaGetranModal) {
        this.$refs.consultaGetranModal.close()
      }
    },
    abrirModificacaoAula(idAluno, schema_aula) {
      this.loadingActions = true

      this.$http.get(urlApi + '/aulas/praticas/detalhes', { params: { id: idAluno, schema: schema_aula } })
        .then(response => {
          if (response.data.success) {
            let aulaPratica = response.data.dados.aulaPratica
            this.aulaModal = {
              aula: {
                aulaPraticaId: aulaPratica.id_aulas_praticas,
                anotacaoPraticaId: aulaPratica.anotacao.id_anotacao_pratica,
                inicio_real: aulaPratica.anotacao.begin_time.slice(11, -3),
                fim_real: aulaPratica.anotacao.finish_time.slice(11, -3),
                hodometro_inicial: aulaPratica.anotacao.hodo_start,
                hodometro_final: aulaPratica.anotacao.hodo_end,
                alunoId: aulaPratica.id_aluno,
                aluno: aulaPratica.nomeAluno,
                renach: aulaPratica.alunoRenach,
                cpf: aulaPratica.cpfAluno,
                instrutorId: aulaPratica.id_instrutor,
                instrutor: aulaPratica.nomeInstrutor,
                categoria: aulaPratica.categoria_cnh,
                data: aulaPratica.data_aula,
                turno: aulaPratica.tipo_aula,
                veiculoId: aulaPratica.id_veiculo,
                schema: schema_aula
              },
              instrutores: response.data.dados.instrutores.map((instrutor) => { return { id: instrutor.id_instrutor, value: instrutor.nome } }),
              veiculos: response.data.dados.veiculos.map((veiculo) => { return { id: veiculo.id_veiculo, value: `${veiculo.placa} / Cat. ${veiculo.categoria_cnh}` } }),
            }
            this.$nextTick(() => {
              this.$refs.aulaModal.open()
            })
          }
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    abrirAulasDoInstrutor(idInstrutor, data, schema_aula) {
      let [dia, mes, ano] = data.slice(0, 10).split('-')

      this.aulasInstrutorModal = null
      setTimeout(() => {
        this.aulasInstrutorModal = {
          schema: schema_aula,
          instrutor: idInstrutor,
          data: `${ano}-${mes}-${dia}`
        }
        this.$nextTick(() => {
          this.$refs.aulasInstrutorModal.open()
        })
      }, 100)
    },
    fecharAulaInstrutor() {
      if (this.$refs.aulasInstrutorModal) {
        this.$refs.aulasInstrutorModal.close()
      }

      this.aulasInstrutorModal = null
    },
    abrirAulasDoAluno(idAluno, aulaPratica, schema_aula) {
      this.$http.get(urlApi + '/alunos/consultar/aulas-praticas', { params: { id: idAluno, schema: schema_aula } })
        .then(response => {
          if (response.data.success) {
            this.aulasAlunoModal = {
              aulas: response.data.dados,
              schema: schema_aula,
              selected: aulaPratica
            }
            this.$nextTick(() => {
              this.$refs.aulasAlunoModal.open()
            })
          }
        })
    },
    updateAndCloseModalAluno() {
      this.closeAulaModal()
    },
    closeAulaModal() {
      if (this.$refs.aulaModal) {
        this.$refs.aulaModal.close()
      }

      this.aulaModal = null
    },
    openConfirmModal(aula) {
      this.$refs.confirmModal.open(aula)
    },
    removerAnotacao(aula) {
      this.$refs.confirmModal.close()

      const { instrutorId, aulaPraticaData, schema } = aula

      this.$http.post(urlApi + '/aulas/praticas/removerAnotacao', { id: idAnotacao, schema: schema_aula })
        .then(response => {
          if (response.data.success) {
            Vue.toasted.show('Anotação removida', {
              icon: 'fa-info',
              position: 'top-right',
              duration: 5000,
              fullWidth: false
            })
            this.removerAula(index)
          }
        })
    },
    removerAula(aulaIndex) {
      this.aulas.splice(aulaIndex, 1)
    },

    async sendClassroom(aula) {
      if (!aula.schema) {
        this.$toasted.show('Erro: Schema não definido', {
          position: 'top-right',
          type: 'error'
        })
        return
      }

      this.loadBtn = true

      const aulaDados = { ...aula, schema: aula.schema }

      try {
        const response = await this.$http.post(`${urlApi}/getran/enviar/aula-pratica`, aulaDados)

        this.$toasted.show(response.data.mensagem, {
          position: 'top-right',
          type: 'success'
        })

        this.modalSend(response.data)

        // Atualizar a lista após envio
        await this.getAulas()
      } catch (error) {
        console.error('Erro ao enviar aula:', error)
        this.modalSend(error.data)
        this.ajuste = true

        this.$toasted.show('Erro ao enviar aula', {
          position: 'top-right',
          type: 'error'
        })
      } finally {
        this.loadBtn = false
      }
    },

    modalSend(aula) {
      this.selectedEnvioModal = aula
      this.envioModal = true
      this.$nextTick(() => {
        this.$refs.envioModal.open()
      })
    },

    async sendByArtisan() {
      this.loading = true

      try {
        await this.$http.post(`${urlApi}/getran/enviar/comand-artisan`)

        this.$toasted.show('Comando artisan executado com sucesso! Aguarde a finalização.', {
          position: 'top-right',
          type: 'success'
        })

        // Atualizar a lista após comando artisan
        if (this.aulas) {
          await this.getAulas()
        }
      } catch (error) {
        console.error('Erro no comando artisan:', error)
        this.modalSend(error.data)

        this.$toasted.show('Erro ao executar comando artisan', {
          position: 'top-right',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    async getCfcs() {
      try {
        const response = await this.$http.get(`${urlApi}/autoescola`)

        this.cfcs = [{ value: '', text: 'Selecione uma autoescola' }]

        // Verificar estrutura response.data.dados (mais comum)
        if (response.data && response.data.dados && Array.isArray(response.data.dados)) {
          response.data.dados.forEach(autoescola => {
            if (autoescola.nome_fantasia && autoescola.nome_fantasia !== 'TESTE') {
              this.cfcs.push({
                value: autoescola.tenant_schema,
                text: autoescola.nome_fantasia,
              })
            }
          })
        } 
        // Fallback para response.body.dados
        else if (response.body && response.body.dados && Array.isArray(response.body.dados)) {
          response.body.dados.forEach(autoescola => {
            if (autoescola.nome_fantasia && autoescola.nome_fantasia !== 'TESTE') {
              this.cfcs.push({
                value: autoescola.tenant_schema,
                text: autoescola.nome_fantasia,
              })
            }
          })
        }
        // Fallback para response.dados
        else if (response.dados && Array.isArray(response.dados)) {
          response.dados.forEach(autoescola => {
            if (autoescola.nome_fantasia && autoescola.nome_fantasia !== 'TESTE') {
              this.cfcs.push({
                value: autoescola.tenant_schema,
                text: autoescola.nome_fantasia,
              })
            }
          })
        }
      } catch (error) {
        console.error('Erro ao carregar autoescolas:', error)
        
        let errorMessage = 'Erro ao carregar autoescolas'
        if (error.body && error.body.mensagem) {
          errorMessage = error.body.mensagem
        } else if (error.body && error.body.msg) {
          errorMessage = error.body.msg
        } else if (error.body && error.body.dados) {
          errorMessage = error.body.dados
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.$toasted.show(errorMessage, {
          position: 'top-right',
          type: 'error',
          duration: 5000
        })
      }
    },

    getStatusText(status) {
      if (!status) return 'PENDENTE'
      return status.toUpperCase()
    },

    getStatusColor(status) {
      if (!status) return 'warning'

      switch (status.toLowerCase()) {
        case 'aceito':
          return 'success'
        default:
          return 'danger'
      }
    },
  },
  watch: {
    selectedStatus(status) {
      this.getAulas()
    }
  }
}
</script>

<style lang="scss" scoped>
.send-container {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  form {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;

    .inputs {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;

      .inputs-row {
        display: flex;
        flex-wrap: wrap;
        gap: 1.5rem;

        .input-full {
          flex: 1;
        }
      }
    }

    .buttons {
      align-self: flex-end;

      display: flex;
      gap: 0.5rem;
    }
  }

  .table-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .pagination-container {
      align-self: flex-end;
    }

    .actions {
      display: flex;
      align-items: center;

      div {
        padding: 0 0.5rem;
        display: flex;
        justify-content: center;

        &:not(:first-child) {
          border-left: 1px solid #ECECEC;
        }

        &:not(:last-child) {
          border-right: 1px solid #ECECEC;
        }
      }
    }
  }
}

.filters-container {
  .filters-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    .filter-row {
      display: flex;
      gap: 1.5rem;
      align-items: flex-start;

      &.primary-row {
        .filter-item.full-width {
          flex: 1;
          max-width: 600px;
          margin: 0 auto;
        }
      }

      &.secondary-row {
        .filter-item {
          flex: 1;
          min-width: 0;

          &.period-filter {
            flex: 1.2;
          }

          &.status-filter {
            flex: 0.8;
          }
        }
      }

      &.tertiary-row {
        .filter-item.student-filter {
          flex: 1;
        }
      }

      @media (max-width: 992px) {
        flex-direction: column;
        gap: 1.25rem;

        .filter-item {
          flex: 1 !important;
          max-width: none !important;
        }
      }
    }

    .filter-section {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding: 1.25rem;
      background: #f8f9fa;
      border-radius: 12px;
      border: 1px solid #e9ecef;
      transition: all 0.2s ease;

      &:hover {
        border-color: #dee2e6;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      }

      .section-title {
        font-size: 0.875rem;
        font-weight: 600;
        color: #2c2c54;
        margin: 0;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .requirement-note {
          font-weight: 400;
          color: #6c757d;
          font-size: 0.75rem;
          text-transform: none;
          letter-spacing: normal;
          font-style: italic;
        }
      }

      .date-range {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;

        @media (max-width: 576px) {
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }
      }

      .student-fields {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;

        @media (max-width: 576px) {
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }

        .input-wrapper {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;

          .field-label {
            font-size: 0.75rem;
            font-weight: 500;
            color: #495057;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .field-input {
            padding: 0.875rem 1rem;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            font-size: 0.875rem;
            transition: all 0.2s ease;
            background-color: #fff;

            &:focus {
              outline: none;
              border-color: #007bff;
              box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.15);
            }

            &:disabled {
              background-color: #f1f3f4;
              border-color: #e9ecef;
              color: #6c757d;
              cursor: not-allowed;
            }

            &::placeholder {
              color: #adb5bd;
            }
          }
        }
      }
    }

    .filter-actions {
      display: flex;
      justify-content: center;
      gap: 1rem;
      padding-top: 1rem;
      border-top: 1px solid #e9ecef;
      margin-top: 0.5rem;

      .action-button {
        min-width: 160px;

        &.primary {
          font-weight: 600;
        }

        &.secondary {
          border: 1px solid #dee2e6;
        }
      }

      @media (max-width: 576px) {
        flex-direction: column;
        align-items: center;

        .action-button {
          width: 100%;
          max-width: 300px;
        }
      }
    }
  }
}

.special-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: center;
  }

  button {
    min-width: 200px;

    @media (max-width: 576px) {
      width: 100%;
      max-width: 300px;
    }
  }
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.aulas-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.pagination-info {
  color: #6c757d;
  font-size: 0.875rem;
  text-align: center;
  margin: 1rem 0;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 1rem;
  min-height: 300px;

  h3 {
    margin: 1rem 0 0.5rem 0;
    color: #202224;
    font-size: 1.5rem;
    font-weight: 600;
  }

  p {
    margin: 0 0 1.5rem 0;
    color: #6c757d;
    font-size: 1rem;
    max-width: 400px;
    line-height: 1.5;
  }
}

// Responsividade
@media (max-width: 992px) {
  .filters-container .filters-wrapper {
    gap: 1.25rem;

    .filter-section {
      padding: 1rem;

      .section-title {
        font-size: 0.8rem;
      }

      .date-range,
      .student-fields {
        gap: 0.75rem;
      }
    }
  }
}

@media (max-width: 576px) {
  .filters-container .filters-wrapper {
    gap: 1rem;

    .filter-section {
      padding: 0.875rem;
      border-radius: 8px;

      .section-title {
        font-size: 0.75rem;

        .requirement-note {
          font-size: 0.7rem;
        }
      }

      .student-fields .input-wrapper .field-input {
        padding: 0.75rem 0.875rem;
        font-size: 0.8rem;
      }
    }

    .filter-actions {
      padding-top: 0.75rem;
      margin-top: 0.25rem;
    }
  }

  .special-actions {
    button {
      font-size: 0.875rem;
      padding: 0.75rem 1rem;
    }
  }
}
</style>
